import { GoalTaskStatus, type WorkflowGoalTask } from '@fruitful/types';
import { sortBy } from 'lodash';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
    type DataTableRowReorderEvent,
} from 'primereact/datatable';
import React from 'react';
import { BodyMediumRegular, IconButton, TrashIcon, YStack } from 'smoothie';

import { TextAreaEditor } from '../../common/components/GridCellEditors';
import { AddNewWorkflowGoalTask } from './AddNewWorkflowGoalTask';

export type WorkflowGoalTaskEphemeralId = WorkflowGoalTask & { id: string };

interface WorkflowGoalTasksSubtableProps {
    tasks: WorkflowGoalTaskEphemeralId[];
    onUpdateTasks: (updatedTasks: WorkflowGoalTaskEphemeralId[]) => void;
    disabled?: boolean;
}

export const WorkflowGoalTasksSubtable = ({
    tasks,
    onUpdateTasks,
    disabled = false,
}: WorkflowGoalTasksSubtableProps) => {
    const updateTasks = (updatedTask: WorkflowGoalTaskEphemeralId) => {
        onUpdateTasks(
            sortBy(
                [
                    ...tasks.filter((task) => task.id !== updatedTask.id),
                    updatedTask,
                ],
                'position',
            ),
        );
    };

    const deleteTask = (task: WorkflowGoalTask) => {
        onUpdateTasks(
            tasks
                .filter((t) => t.description !== task.description)
                .map((t, index) => ({ ...t, position: index })),
        );
    };

    const onRowReorder = ({
        value: reorderedTasks,
    }: DataTableRowReorderEvent<WorkflowGoalTaskEphemeralId[]>) => {
        onUpdateTasks(
            reorderedTasks.map((task, index) => ({ ...task, position: index })),
        );
    };

    const onRowEditComplete = ({ newData }: DataTableRowEditCompleteEvent) => {
        updateTasks(newData as WorkflowGoalTaskEphemeralId);
    };

    return (
        <YStack paddingLeft={32}>
            <DataTable
                value={tasks}
                size="small"
                reorderableRows={!disabled}
                onRowReorder={disabled ? undefined : onRowReorder}
                editMode={disabled ? undefined : 'row'}
                onRowEditComplete={disabled ? undefined : onRowEditComplete}
                showHeaders={false}
                // emptyMessage hides default message when passed a truthy value
                emptyMessage={true}
            >
                <Column
                    field="position"
                    sortable
                    style={{ width: '2rem', fontSize: '0.875rem' }}
                    body={({ position }: WorkflowGoalTaskEphemeralId) => (
                        <BodyMediumRegular color="$gray1100">
                            {position + 1}.
                        </BodyMediumRegular>
                    )}
                />
                <Column
                    field="description"
                    style={{ fontSize: '0.875rem' }}
                    editor={
                        disabled
                            ? undefined
                            : (options) => <TextAreaEditor options={options} />
                    }
                />
                {!disabled && <Column rowEditor style={{ width: '2rem' }} />}
                {!disabled && (
                    <Column
                        style={{ width: '2rem' }}
                        body={(task: WorkflowGoalTask) => (
                            <YStack justifyContent="center" alignItems="center">
                                <IconButton
                                    variant="tertiary"
                                    tone="danger"
                                    icon={TrashIcon}
                                    onPress={() => {
                                        deleteTask(task);
                                    }}
                                />
                            </YStack>
                        )}
                    />
                )}
                {!disabled && <Column rowReorder style={{ width: '2rem' }} />}
            </DataTable>
            {!disabled && (
                <AddNewWorkflowGoalTask
                    onAdd={(task: { description: string }) => {
                        updateTasks({
                            ...task,
                            id: task.description,
                            status: GoalTaskStatus.IN_PROGRESS,
                            position: tasks.length,
                        });
                    }}
                />
            )}
        </YStack>
    );
};
